import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  CORE_MODULE_LABELS,
  MODULE_DEFINITIONS,
  SWITCHABLE_MODULES,
  moduleDefinition,
  moduleDependents,
  needsInWords,
  planModuleChange,
  planRevert,
  planSwitchOnWith,
  type AppSettingsView,
  type ModuleChangePlan,
  type ModuleDefinition,
  type ModuleDependent,
  type ModuleKey,
  type ModuleLastChange,
  type ModuleState,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { Switch } from '../../../components/Switch';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useAppSettings } from '../../../lib/app-settings';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { modulesIn } from '../../../lib/modules';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { AutoReadingCard, MigrationCard } from '../../icp/components/MigrationCards';
import { useModulesView, useSaveModules, type ModuleChange } from '../api/settings-api';
import styles from './MasterSettingsPage.module.css';

/** At the tablet breakpoint and below the pane would sit far from the tile, so it opens inside the tile instead. */
const NARROW_MEDIA = '(max-width: 900px)';

/**
 * A switch waiting for an answer before it moves: either it carries others on or off with it, or it needs one of
 * several and none is on, so the Admin picks which (§L28: that choice is theirs). Which of the two is worked out from
 * the switches as they are when drawn, so a change made meanwhile by someone else is never answered from old facts.
 */
interface Question {
  readonly module: ModuleKey;
  readonly enabled: boolean;
}

/**
 * ADMIN-17 / F21.01 (§L28, §L34; Admin redesign 2026-11-02, Option A of `5-settings/master-settings.html`): every part
 * of the app as a tile, grouped On by default, Off by default and Always on, with a pane explaining the one touched.
 *
 * - A switch with no knock-on just happens, with a 10-second Undo that puts back that switch alone (§5a rule 8).
 * - A switch that carries others, or needs one of several, asks inside its tile; the switch waits half-way until the
 *   answer, never showing a state nobody agreed to. The tile stays where it is (review W12: widening it moved every
 *   tile after it); only a choice between parts, whose answers are long, takes the whole row.
 * - Off hides a part everywhere and deletes nothing. Admin only (§L23).
 */
export function MasterSettingsPage(): ReactNode {
  const canView = useCanView('admin_config');
  const settings = useAppSettings();
  if (!canView) return <ForbiddenState resource="Master settings" contact="Your admin" />;
  if (settings.isPending) return <SkeletonLines count={12} />;
  if (settings.error) return <ServerErrorState onRetry={() => void settings.refetch()} />;
  const onCount = SWITCHABLE_MODULES.filter((module) => settings.data.modules[module]).length;
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>Master settings</h1>
          <p className={styles.lede}>
            Switch parts of the app on or off for everyone. Off hides a part everywhere — menus, tabs, screens and its emails — and keeps all its data, so
            switching it back on brings it back as it was.
          </p>
        </div>
        <ToneChip tone="done">
          {onCount} of {SWITCHABLE_MODULES.length} parts on
        </ToneChip>
      </header>
      <ModuleBoard view={settings.data} />
      <section className={styles.group} aria-labelledby="ranking-cards">
        <div className={styles.groupHead}>
          <h2 id="ranking-cards" className={styles.groupTitle}>
            Ranking by the ideal candidate
          </h2>
          <span className={styles.groupMeta}>Part of Ranking, which is always on: every job ranks this way</span>
        </div>
        {/* ADR 0062 §6: ranking by the ideal candidate is core, so these two cards are not behind a module switch. */}
        <div className={styles.rankingCards}>
          <MigrationCard />
          <AutoReadingCard />
        </div>
      </section>
    </div>
  );
}

function ModuleBoard({ view }: { readonly view: AppSettingsView }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const save = useSaveModules();
  const history = useModulesView();
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const isNarrow = useMediaQuery(NARROW_MEDIA);
  const [selected, setSelected] = useState<ModuleKey | null>(null);
  const [asked, setAsked] = useState<Question | null>(null);
  const modules = view.modules;
  // Someone else may make the change while the question is open; then there is nothing left to ask.
  const question = asked !== null && modules[asked.module] !== asked.enabled ? asked : null;

  const undo = (previous: readonly ModuleChange[]): void => {
    const revert = planRevert(modulesIn(queryClient), previous);
    if (revert.changes.length === 0) return;
    if (revert.problem) {
      show({ tone: 'warning', message: "Couldn't undo it", detail: `${revert.problem} Switch that on first.` });
      return;
    }
    save.mutate({ changes: revert.changes }, { onError: (cause) => show({ tone: 'danger', message: "Couldn't undo it", detail: errorMessage(cause) }) });
  };

  const apply = (module: ModuleKey, plan: ModuleChangePlan): void => {
    const moved = [module, ...plan.alsoChanged];
    const changes = moved.map((key) => ({ module: key, enabled: plan.next[key] }));
    const previous = moved.map((key) => ({ module: key, enabled: modules[key] }));
    const enabled = plan.next[module];
    setAsked(null);
    save.mutate(
      { changes },
      {
        onSuccess: () =>
          show({
            tone: 'success',
            message: `${moduleDefinition(module).label} switched ${enabled ? 'on' : 'off'}`,
            detail: plan.alsoChanged.length > 0 ? `Also switched ${enabled ? 'on' : 'off'}: ${labelsOf(plan.alsoChanged)}.` : undefined,
            action: { label: 'Undo', run: () => undo(previous) },
          }),
        onError: (cause) => show({ tone: 'danger', message: "Couldn't switch it", detail: errorMessage(cause) }),
      },
    );
  };

  const toggle = (module: ModuleKey, enabled: boolean): void => {
    setSelected(module);
    // The waiting switch is the question's own: pressing it again takes the question back.
    if (question?.module === module) {
      setAsked(null);
      return;
    }
    const plan = planModuleChange(modules, module, enabled);
    if (plan.blockedBy === null && plan.alsoChanged.length === 0) {
      apply(module, plan);
      return;
    }
    setAsked({ module, enabled });
  };

  const tile = (definition: ModuleDefinition): ReactNode => {
    const isSelected = selected === definition.key;
    const isAsking = question?.module === definition.key;
    return (
      <ModuleTile
        key={definition.key}
        definition={definition}
        modules={modules}
        canEdit={canEdit}
        isSelected={isSelected}
        question={isAsking ? question : null}
        onSelect={() => setSelected(definition.key)}
        onToggle={(enabled) => toggle(definition.key, enabled)}
        onAnswer={(plan) => apply(definition.key, plan)}
        onCancel={() => setAsked(null)}
        pane={
          isNarrow && isSelected ? <ModulePane definition={definition} modules={modules} isWaiting={isAsking} lastChanged={lastChangeOf(history, definition.key)} isLoading={history.isPending} isUnread={history.isError} /> : null
        }
      />
    );
  };

  const selectedDefinition = selected === null ? null : moduleDefinition(selected);
  return (
    <div className={styles.board}>
      <div className={styles.groups}>
        <ModuleGroup title="On by default" meta="Parts of the hiring workflow">
          {MODULE_DEFINITIONS.filter((definition) => definition.isOnByDefault).map(tile)}
        </ModuleGroup>
        <ModuleGroup title="Off by default" meta="Not part of the hiring workflow yet: switch one on when the team is ready">
          {MODULE_DEFINITIONS.filter((definition) => !definition.isOnByDefault).map(tile)}
        </ModuleGroup>
        <AlwaysOn />
      </div>
      {!isNarrow && (
        <aside className={styles.pane} aria-label="About the part you picked">
          {selectedDefinition ? (
            <ModulePane
              definition={selectedDefinition}
              modules={modules}
              isWaiting={question?.module === selectedDefinition.key}
              lastChanged={lastChangeOf(history, selectedDefinition.key)}
              isLoading={history.isPending}
              isUnread={history.isError}
            />
          ) : (
            <p className={styles.paneEmpty}>Pick a part to see what it does, what switching it off hides, what it needs and who changed it last.</p>
          )}
        </aside>
      )}
    </div>
  );
}

function lastChangeOf(history: ReturnType<typeof useModulesView>, module: ModuleKey): ModuleLastChange | null {
  return history.data?.lastChanged[module] ?? null;
}

function ModuleGroup({ title, meta, children }: { readonly title: string; readonly meta: string; readonly children: ReactNode }): ReactNode {
  const id = useId();
  return (
    <section className={styles.group} aria-labelledby={id}>
      <div className={styles.groupHead}>
        <h2 id={id} className={styles.groupTitle}>
          {title}
        </h2>
        <span className={styles.groupMeta}>{meta}</span>
      </div>
      <ul className={styles.tiles}>{children}</ul>
    </section>
  );
}

interface ModuleTileProps {
  readonly definition: ModuleDefinition;
  readonly modules: ModuleState;
  readonly canEdit: boolean;
  readonly isSelected: boolean;
  readonly question: Question | null;
  readonly onSelect: () => void;
  readonly onToggle: (enabled: boolean) => void;
  readonly onAnswer: (plan: ModuleChangePlan) => void;
  readonly onCancel: () => void;
  /** The pane, drawn inside the tile on a tablet or phone. */
  readonly pane: ReactNode;
}

function ModuleTile({ definition, modules, canEdit, isSelected, question, onSelect, onToggle, onAnswer, onCancel, pane }: ModuleTileProps): ReactNode {
  const questionId = useId();
  const isOn = modules[definition.key];
  const isWaiting = question !== null;
  const isChoice = question !== null && planModuleChange(modules, question.module, question.enabled).blockedBy !== null;
  const needs = definition.needs !== undefined || definition.needsOneOf !== undefined ? needsInWords(definition) : null;
  return (
    <li className={styles.tile} data-on={isOn} data-selected={isSelected} data-wide={isChoice || pane !== null}>
      <div className={styles.tileMain}>
        <button type="button" className={styles.tileBody} aria-pressed={isSelected} onClick={onSelect}>
          <span className={styles.tileName}>{definition.label}</span>
          <span className={styles.tileText}>{definition.description}</span>
        </button>
        <div className={styles.tileSwitch}>
          <span className={styles.tileState} aria-hidden>
            {stateWord(isOn, isWaiting)}
          </span>
          <Switch checked={isOn} isWaiting={isWaiting} disabled={!canEdit} ariaLabel={definition.label} describedBy={isWaiting ? questionId : undefined} onChange={onToggle} />
        </div>
        {/* The line is kept on every tile so a row of tiles lines up (critique: tile heights differ). */}
        <span className={styles.tileNeeds}>{needs !== null && <span className={styles.needs}>Needs {needs}</span>}</span>
      </div>
      {question && <QuestionBox id={questionId} definition={definition} modules={modules} question={question} onAnswer={onAnswer} onCancel={onCancel} />}
      {pane !== null && <div className={styles.tilePane}>{pane}</div>}
    </li>
  );
}

function stateWord(isOn: boolean, isWaiting: boolean): string {
  if (isWaiting) return 'Waiting';
  return isOn ? 'On' : 'Off';
}

interface QuestionBoxProps {
  readonly id: string;
  readonly definition: ModuleDefinition;
  readonly modules: ModuleState;
  readonly question: Question;
  readonly onAnswer: (plan: ModuleChangePlan) => void;
  readonly onCancel: () => void;
}

/** The question a waiting switch asks, inside its own tile (CLAUDE.md §5: beside the control that opened it). */
function QuestionBox({ id, definition, modules, question, onAnswer, onCancel }: QuestionBoxProps): ReactNode {
  const firstAnswer = useRef<HTMLButtonElement>(null);
  useEffect(() => firstAnswer.current?.focus(), []);
  const plan = planModuleChange(modules, question.module, question.enabled);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    onCancel();
  };
  const answers =
    plan.blockedBy === null
      ? [{ key: 'confirm', label: `Switch ${plan.alsoChanged.length > 0 ? 'them' : 'it'} ${question.enabled ? 'on' : 'off'}`, plan }]
      : plan.choices.flatMap((choice) => {
          const withChoice = planSwitchOnWith(modules, question.module, choice);
          if (withChoice.blockedBy !== null) return [];
          const named = [choice, ...withChoice.alsoChanged.filter((module) => module !== choice)];
          return [{ key: choice, label: `Switch on ${labelsOf(named, 'and')}`, plan: withChoice }];
        });
  return (
    <div className={styles.question} role="group" aria-labelledby={id} onKeyDown={onKeyDown}>
      <p id={id} className={styles.questionText}>
        {questionWords(definition, question, plan)}
      </p>
      <div className={styles.questionActions}>
        <Button variant="quiet" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        {answers.map((answer, index) => (
          <Button key={answer.key} ref={index === 0 ? firstAnswer : undefined} variant={index === 0 ? 'solid' : 'outline'} size="sm" onClick={() => onAnswer(answer.plan)}>
            {answer.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** §L28: the question names every part that moves with this one, and why. */
function questionWords(definition: ModuleDefinition, question: Question, plan: ModuleChangePlan): ReactNode {
  if (plan.blockedBy !== null) {
    return (
      <>
        <strong>{definition.label}</strong> needs <NameList modules={plan.choices} joiner="or" />, and {plan.choices.length === 2 ? 'neither is' : 'none of them is'} on. Which
        should come on with it?
      </>
    );
  }
  if (plan.alsoChanged.length === 0) {
    return (
      <>
        Switch <strong>{definition.label}</strong> {question.enabled ? 'on' : 'off'}?
      </>
    );
  }
  if (!question.enabled) {
    return (
      <>
        Switching <strong>{definition.label}</strong> off also switches off <NameList modules={plan.alsoChanged} />, which can't work without it. Nothing
        is deleted.
      </>
    );
  }
  const followers = plan.alsoChanged.filter((module) => moduleDefinition(module).comesOnWith !== undefined && !(definition.needs ?? []).includes(module));
  const needed = plan.alsoChanged.filter((module) => !followers.includes(module));
  return (
    <>
      Switching <strong>{definition.label}</strong> on also switches on{' '}
      {needed.length > 0 && (
        <>
          <NameList modules={needed} />, which it needs
        </>
      )}
      {needed.length > 0 && followers.length > 0 && ', and '}
      {followers.length > 0 && (
        <>
          <NameList modules={followers} />, which comes on with it
        </>
      )}
      .
    </>
  );
}

interface ModulePaneProps {
  readonly definition: ModuleDefinition;
  readonly modules: ModuleState;
  readonly isWaiting: boolean;
  readonly lastChanged: ModuleLastChange | null;
  readonly isLoading: boolean;
  /** "Last changed" failed to load: never shown as "—", which would mean nobody ever switched it. */
  readonly isUnread: boolean;
}

/** What the touched part does, what goes when it's off, what it needs and what needs it, and who last switched it. */
function ModulePane({ definition, modules, isWaiting, lastChanged, isLoading, isUnread }: ModulePaneProps): ReactNode {
  const isOn = modules[definition.key];
  const dependents = moduleDependents(definition.key);
  return (
    <div className={styles.paneBody}>
      <div className={styles.paneHead}>
        <h2 className={styles.paneTitle}>{definition.label}</h2>
        {isWaiting ? <ToneChip tone="attention">Waiting for your answer</ToneChip> : <ToneChip tone={isOn ? 'done' : 'ended'}>{isOn ? 'On' : 'Off'}</ToneChip>}
      </div>
      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>What it does</dt>
          <dd>{definition.description}</dd>
        </div>
        <div className={styles.fact}>
          <dt>When off, these are hidden</dt>
          <dd>{definition.hides}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Needs</dt>
          <dd>{needsLine(definition, modules)}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Needed by</dt>
          <dd>{dependents.length === 0 ? EMPTY : dependents.map((dependent) => neededByWords(dependent)).join('; ')}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Last changed</dt>
          <dd className={styles.data}>{lastChangedCell(lastChanged, isLoading, isUnread)}</dd>
        </div>
      </dl>
      <p className={styles.note}>Off never deletes anything. Switching back on brings it back as it was, with its history.</p>
    </div>
  );
}

function needsLine(definition: ModuleDefinition, modules: ModuleState): ReactNode {
  const all = (definition.needs ?? []).map((key) => <NeedChip key={key} module={key} isOn={modules[key]} />);
  const oneOf = definition.needsOneOf ?? [];
  if (all.length === 0 && oneOf.length === 0) return EMPTY;
  return (
    <span className={styles.needsLine}>
      {all}
      {oneOf.map((key, index) => (
        <span key={key} className={styles.needsLine}>
          {index > 0 && <span>or</span>}
          <NeedChip module={key} isOn={modules[key]} />
        </span>
      ))}
    </span>
  );
}

function NeedChip({ module, isOn }: { readonly module: ModuleKey; readonly isOn: boolean }): ReactNode {
  return (
    <span className={styles.needs} data-on={isOn}>
      {moduleDefinition(module).label} · {isOn ? 'on' : 'off'}
    </span>
  );
}

function neededByWords({ module, kind }: ModuleDependent): string {
  const definition = moduleDefinition(module);
  if (kind === 'needs_one_of') return `${definition.label} (it needs ${needsInWords(definition)})`;
  if (kind === 'comes_on_with') return `${definition.label} (it comes on with this one)`;
  return definition.label;
}

function lastChangedCell(change: ModuleLastChange | null, isLoading: boolean, isUnread: boolean): ReactNode {
  if (isLoading) return <Skeleton width="12rem" />;
  if (isUnread) return "Couldn't load who changed it";
  return lastChangedWords(change);
}

/** "Switched off by Nasreen Akter · 08-10-2026 11:02 am GMT+6", from the event log (non-negotiable #8: with its zone). */
function lastChangedWords(change: ModuleLastChange | null): string {
  if (change === null) return EMPTY;
  const by = change.byName === null ? '' : ` by ${change.byName}`;
  return `Switched ${change.enabled ? 'on' : 'off'}${by} · ${formatDateTime(change.at)}`;
}

/** §L34's ten core parts, with what each covers: they have no switch. */
function AlwaysOn(): ReactNode {
  return (
    <section className={styles.group} aria-labelledby="always-on">
      <div className={styles.groupHead}>
        <h2 id="always-on" className={styles.groupTitle}>
          Always on
        </h2>
        <span className={styles.groupMeta}>The core of the app: {CORE_MODULE_LABELS.length} parts, no switch</span>
      </div>
      <ul className={styles.core}>
        {CORE_MODULE_LABELS.map((core) => (
          <li key={core.label} className={styles.coreItem}>
            <span className={styles.coreName}>{core.label}</span>
            <span className={styles.coreText}>{core.description}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Each name in bold on its own, so "Hire requests and Exits and backfill" still reads as two parts. */
function NameList({ modules, joiner = 'and' }: { readonly modules: readonly ModuleKey[]; readonly joiner?: 'and' | 'or' }): ReactNode {
  return modules.map((module, index) => (
    <span key={module}>
      {index > 0 && (index === modules.length - 1 ? ` ${joiner} ` : ', ')}
      <strong>{moduleDefinition(module).label}</strong>
    </span>
  ));
}

/** "Hire requests, Organisation" → "Hire requests and Organisation" (or "or"). */
function labelsOf(modules: readonly ModuleKey[], joiner: 'and' | 'or' = 'and'): string {
  const names = modules.map((module) => moduleDefinition(module).label);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} ${joiner} ${names[names.length - 1] ?? ''}`;
}
